import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Button,
  Input,
  Col,
  Row,
  Container,
  Label,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormGroup,
} from "reactstrap";
import api from "../../api";
import Constant from "../../constants";
import "./index.css";
import Loading from "../../components/Loading";
import Select from "react-select";
import { ApiResponse, SelectOption } from "model/response";
import { UserInfo } from "model/user";
import * as utils from "resources/utils"


export default function Home() {
  const [currentUser, setCurrentUser] = useState<UserInfo | null>(null);
  const [modal, setModal] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);

  const [companyOptions, setCompanyOptions] = useState<Array<SelectOption>>([]);
  const [selectedCompany, setSelectedCompany] = useState<
    SelectOption | null | undefined
  >(null);

  // permissions
  const roleAccessAdvancedFeatures = ["Admin", "Company Admin", "Data Practitioner"];
  const canAccessAdvancedFeatures = currentUser?.roles.some(userRole => roleAccessAdvancedFeatures.includes(userRole.name));

  const initData = async () => {
    const user = api.getCurrentUser();
    setCurrentUser(user);
    setLoading(false);
  };

  const setCompany = async () => {
    if (currentUser) {
      const options = currentUser?.companies?.map((company) => {
        return {
          label: company.name,
          value: company.id,
        };
      });
      setCompanyOptions(options);
      if (currentUser?.companies.length > 1) {
        const currentCompanyId = await api.getCurrentCompanyId();
        if (!currentCompanyId) {
          setModal(true);
        } else {
          const company = options.find(
            (company) => company?.value == Number(currentCompanyId)
          );
          setSelectedCompany(company);
        }
      } else {
        const companyId: any = currentUser.companies[0] ? currentUser.companies[0].id : 0;
        utils.default.setCookies("company_id", companyId)
      }
    }
  };

  const setCurrentCompanyId = async () => {
    if (selectedCompany) {
      utils.default.setCookies("company_id", selectedCompany?.value);
      setModal(false);
    }
  };

  useEffect(() => {
    initData();
  }, []);

  useEffect(() => {
    setCompany();
  }, [currentUser]);

  if (loading) return <Loading />;

  return (
    <Container>
      <Row>
        <Col
          xs={12}
          md={4}
          tag={Link}
          to="/dictionary"
          style={styles.block}
        >
          <div style={styles.item}>
            <i className="material-icons" style={styles.icon}>
              book
            </i>
            <Label style={styles.title}>Dictionary</Label>
          </div>
        </Col>
        {(currentUser?.is_admin || currentUser?.is_company_admin) && (
            <Col xs={12} md={4} tag={Link} to="/create_user" style={styles.block}>
              <div style={styles.item}>
                <i className="material-icons" style={styles.icon}>
                  people
                </i>
                <Label style={styles.title}>Create New User</Label>
              </div>
            </Col>
          )}
          <Col
            xs={12}
            md={4}
            tag={Link}
            to="/reset_password"
            style={styles.block}
          >
            <div style={styles.item}>
              <i className="material-icons" style={styles.icon}>
                lock
              </i>
              <Label style={styles.title}>Reset password</Label>
            </div>
          </Col>
          {companyOptions.length > 1 && (
            <Col
              xs={12}
              md={4}
              onClick={() => {
                setModal(true);
              }}
              style={styles.block}
            >
              <div style={styles.item}>
                <i className="material-icons" style={styles.icon}>
                  business
                </i>
                <Label style={styles.title}>Change Company</Label>
              </div>
            </Col>
          )}
        {canAccessAdvancedFeatures &&
          <Col xs={12} md={4} tag={Link} to="/datasources" style={styles.block}>
            <div style={styles.item}>
              <i className="material-icons" style={styles.icon}>
                folder
              </i>
              <Label style={styles.title}>Datasources</Label>
            </div>
        </Col>
        }
        {canAccessAdvancedFeatures &&
          <Col
            xs={12}
            md={4}
            tag={Link}
            to="/schedule_queries"
            style={styles.block}
          >
            <div style={styles.item}>
              <i className="material-icons" style={styles.icon}>
                query_builder
              </i>
              <Label style={styles.title}>Schedule Queries</Label>
            </div>
          </Col>
        }
        {canAccessAdvancedFeatures &&
          <Col
            xs={12}
            md={4}
            tag={Link}
            to="/transformations"
            style={styles.block}
          >
            <div style={styles.item}>
              <i className="material-icons" style={styles.icon}>
                transform
              </i>
              <Label style={styles.title}>Transformation</Label>
            </div>
          </Col>
        }
        {canAccessAdvancedFeatures &&
          <Col
            xs={12}
            md={4}
            tag={Link}
            to="/data_pipeline"
            style={styles.block}
          >
            <div style={styles.item}>
              <i className="material-icons" style={styles.icon}>
                mediation
              </i>
              <Label style={styles.title}>Data pipelines</Label>
            </div>
          </Col>
        }
        {canAccessAdvancedFeatures &&
          <Col
            xs={12}
            md={4}
            tag={Link}
            to="/monitoring"
            style={styles.block}
          >
            <div style={styles.item}>
              <i className="material-icons" style={styles.icon}>
                area_chart
              </i>
              <Label style={styles.title}>Monitoring (beta)</Label>
            </div>
          </Col>
        }
      </Row>

      <div>
        <Modal isOpen={modal}>
          <ModalHeader>Notice</ModalHeader>
          <ModalBody>
            Your account is linked with multiple companies! Please select a company for this session.
          </ModalBody>
          <Col xs={12}>
            <FormGroup>
              <Label>Select a company</Label>
              <Select
                isDisabled={false}
                options={companyOptions}
                value={selectedCompany}
                onChange={(selectedOption: any) => {
                  setSelectedCompany(selectedOption);
                }}
              />
            </FormGroup>
          </Col>
          <ModalFooter>
            <Button color="primary" onClick={setCurrentCompanyId}>
              Done
            </Button>{" "}
          </ModalFooter>
        </Modal>
      </div>
    </Container>
  );
}

const styles: { [name: string]: React.CSSProperties } = {
  block: {
    marginTop: 20,
  },
  item: {
    borderWidth: 1,
    borderStyle: "solid",
    borderColor: "var(--color-accent-dark)",
    borderRadius: 5,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    paddingLeft: 12,
    paddingRight: 12,
    paddingTop: 25,
    paddingBottom: 25,
  },
  title: {
    marginTop: 15,
    fontWeight: 1000,
    fontSize: 20,
    color: "var(--color-accent-dark)",
  },
  icon: {
    fontSize: 36,
    color: "var(--color-accent-dark)",
  },
};
